import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserX, Search, RefreshCw, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import StoppedCustomersList, { formatRupiah } from './StoppedCustomersList';
import StopCustomerDialog from './StopCustomerDialog';
import { STOPPED_CHANGED_EVENT } from './CustomerListTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 50;
const MONTHS = [{ value: 2, label: '> 2 bln' }, { value: 3, label: '> 3 bln' }, { value: 6, label: '> 6 bln' }];
const STATUSES = [{ value: 'pending', label: 'Perlu Keputusan' }, { value: 'stopped', label: 'Sudah Berhenti' }];
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Segmented = ({ options, value, onChange, testPrefix }) => (
  <div className="inline-flex p-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700">
    {options.map((o) => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testPrefix}-${o.value}`}
        className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors duration-200 ${value === o.value ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

const Stat = ({ label, value, testId, tone }) => (
  <div className="px-4 py-3 rounded-xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-700">
    <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
    <div className={`text-lg sm:text-xl font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

export default function StoppedCustomersPanel({ connectionType, canStop, onOpenCustomer }) {
  const [months, setMonths] = useState(2);
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ rows: [], total: 0, outstanding: 0 });
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ months: String(months), status, connection_type: connectionType || '', limit: String(PAGE), offset: String(page * PAGE) });
      if (query) p.set('search', query);
      const r = await axios.get(`${API_URL}/api/v2/stopped-customers?${p}`, auth());
      setData({ rows: r.data?.data || [], total: r.data?.total || 0, outstanding: r.data?.total_outstanding || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat daftar pelanggan berhenti');
    } finally {
      setLoading(false);
    }
  }, [months, status, connectionType, page, query]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / PAGE));
  useEffect(() => { if (!loading && page >= pages) setPage(pages - 1); }, [loading, page, pages]);

  const confirmStop = async () => {
    setStopping(true);
    try {
      const r = await axios.post(`${API_URL}/api/v2/stopped-customers/${target.id}/stop`, {}, auth());
      toast.success(r.data?.message || 'Pelanggan diputuskan');
      setTarget(null);
      window.dispatchEvent(new Event(STOPPED_CHANGED_EVENT));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memutuskan pelanggan');
    } finally {
      setStopping(false);
    }
  };

  const changeFilter = (setter) => (v) => { setter(v); setPage(0); };

  return (
    <Card className="border-0 shadow-xl bg-white/90 dark:bg-slate-900/80 backdrop-blur-sm ring-1 ring-slate-200/50 dark:ring-slate-700/50" data-testid="stopped-customers-panel">
      <CardContent className="pt-5 pb-5 space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center flex-shrink-0">
            <UserX className="w-5 h-5 text-rose-600 dark:text-rose-400" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-white">Pelanggan Berhenti</h2>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Pelanggan terisolir yang tagihan tertuanya belum dibayar lebih dari {months} bulan. Putuskan untuk menghentikan layanan — tagihan tetap tercatat sebagai piutang.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Segmented options={STATUSES} value={status} onChange={changeFilter(setStatus)} testPrefix="stopped-status" />
          <Segmented options={MONTHS} value={months} onChange={changeFilter(setMonths)} testPrefix="stopped-months" />
          <div className="relative flex-1 min-w-[200px] sm:max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama, no. akun, PPPoE" className="pl-9 h-9" data-testid="stopped-search-input" />
          </div>
          <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="stopped-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:max-w-md">
          <Stat label="Pelanggan" value={data.total.toLocaleString('id-ID')} testId="stopped-total-count" tone="text-slate-900 dark:text-white" />
          <Stat label="Total Tunggakan" value={formatRupiah(data.outstanding)} testId="stopped-total-outstanding" tone="text-rose-600 dark:text-rose-400" />
        </div>

        {loading && !data.rows.length ? (
          <div className="py-16 flex justify-center text-slate-400" data-testid="stopped-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : data.rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="stopped-empty">
            {status === 'pending' ? `Tidak ada pelanggan terisolir lebih dari ${months} bulan.` : 'Belum ada pelanggan berstatus Berhenti dengan tunggakan ini.'}
          </div>
        ) : (
          <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <StoppedCustomersList rows={data.rows} canStop={canStop && status === 'pending'} onStop={setTarget} onOpen={onOpenCustomer} />
          </div>
        )}

        {data.total > PAGE && (
          <div className="flex items-center justify-end gap-2 text-xs text-slate-500" data-testid="stopped-pagination">
            <Button variant="outline" size="sm" disabled={page === 0 || loading} onClick={() => setPage(page - 1)} data-testid="stopped-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
            <span data-testid="stopped-page-info">Hal {page + 1} dari {pages}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= pages || loading} onClick={() => setPage(page + 1)} data-testid="stopped-next-page"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </CardContent>
      <StopCustomerDialog customer={target} loading={stopping} onCancel={() => setTarget(null)} onConfirm={confirmStop} />
    </Card>
  );
}
